import {Card, NotificationCenter, Segment} from 'platform/components';
import {Box, Grid, Heading, Show, Space, Stack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';

import {noop} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {TaskCenterList} from '@dms/task-manager';

import {DASHBOARD_TILE_PREVIEW_FIXTURES} from '../../consts/dashboardTilePreviewFixtures';
import {OPPORTUNITIES_FUNNEL_SEGMENTS} from '../../consts/opportunitiesFunnelSegments';
import {SEGMENT_MIN_WIDTH} from '../../consts/segmentMinWidth';
import {DashboardHiddenTile} from '../../types/DashboardHiddenTile';
import {createOpportunitiesFunnelColumns} from '../../utils/createOpportunitiesFunnelColumns';
import {LatestBusinessCaseRows} from '../LatestBusinessCases/LatestBusinessCaseRows';
import {LatestInterestRows} from '../LatestInterests/LatestInterestRows';
import {LatestVehicleRows} from '../LatestVehicles/LatestVehicleRows';
import {OpportunitiesFunnelMetrics} from '../OpportunitiesFunnelMetrics';
import {RecommendedActions} from '../RecommendedActions';
import {Support} from '../Support';
import {AggregateMotWarnings} from '../VehicleWarehouse/AggregateMotWarnings';
import {getColumns} from '../VehicleWarehouse/utils/getColumns';
import {WarehouseMetrics} from '../VehicleWarehouse/WarehouseMetrics';
import {WORKSHOP_OVERVIEW_DENSITY_BY_SIZE} from '../WorkshopOverview/constants/workshopOverviewDensityBySize';
import {WorkshopOverviewCompact} from '../WorkshopOverview/WorkshopOverviewCompact';
import {WorkshopOverviewSection} from '../WorkshopOverview/WorkshopOverviewSection';

const PREVIEW_WORKSHOP_INTERACTION = {
  isLoading: false,
  pendingTileId: null,
  onOpenTile: noop,
  onPrefetchTile: noop,
};

export function renderDashboardTilePreview(tile: DashboardHiddenTile): ReactNode {
  return match(tile.id)
    .with('latest-vehicles', () => (
      <Card title={i18n.t('page.homepage.labels.latestVehicles')} hasSeparator={false} isFullHeight>
        <Box minHeight={56} height="100%">
          <LatestVehicleRows
            vehicles={DASHBOARD_TILE_PREVIEW_FIXTURES.latestVehicles}
            size={tile.previewSize}
            naturalRows={4}
            isPreview
          />
        </Box>
      </Card>
    ))
    .with('vehicle-warehouse', () => (
      <Card
        title={i18n.t('page.homepage.labels.vehicleWarehouse')}
        hasSeparator={false}
        isFullHeight
      >
        <VStack spacing={4} height="100%">
          <WarehouseMetrics
            columns={getColumns(DASHBOARD_TILE_PREVIEW_FIXTURES.vehicleStockOverview)}
            isRedirecting={false}
            variant="default"
            onOpenMetric={noop}
          />
          <AggregateMotWarnings
            canReadVehicle
            isViewLoading={false}
            expiredCount={DASHBOARD_TILE_PREVIEW_FIXTURES.vehicleStockOverview.motExpiredWarning}
            expiringCount={
              DASHBOARD_TILE_PREVIEW_FIXTURES.vehicleStockOverview.motExpirationWarning
            }
            isAllMotValid={false}
            onViewExpired={noop}
            onViewExpiring={noop}
          />
        </VStack>
      </Card>
    ))
    .with('workshop-overview', () => (
      <Card
        hasSeparator={false}
        title={i18n.t('page.homepage.workshopOverview.title')}
        isFullHeight
      >
        {WORKSHOP_OVERVIEW_DENSITY_BY_SIZE[tile.previewSize] === 'compact' ? (
          <WorkshopOverviewCompact
            sections={DASHBOARD_TILE_PREVIEW_FIXTURES.workshopSections}
            interaction={PREVIEW_WORKSHOP_INTERACTION}
          />
        ) : (
          <Grid
            columns={[
              1,
              1,
              DASHBOARD_TILE_PREVIEW_FIXTURES.workshopSections.length,
              DASHBOARD_TILE_PREVIEW_FIXTURES.workshopSections.length,
            ]}
            spacing={4}
          >
            {DASHBOARD_TILE_PREVIEW_FIXTURES.workshopSections.map((sectionState) => (
              <WorkshopOverviewSection
                key={sectionState.section}
                section={sectionState.section}
                tiles={sectionState.tiles}
                interaction={PREVIEW_WORKSHOP_INTERACTION}
              />
            ))}
          </Grid>
        )}
      </Card>
    ))
    .with('latest-business-cases', () => (
      <Card
        title={i18n.t('page.homepage.labels.latestBusinessCases')}
        hasSeparator={false}
        isFullHeight
      >
        <Box minHeight={45} height="100%">
          <LatestBusinessCaseRows
            businessCases={DASHBOARD_TILE_PREVIEW_FIXTURES.latestBusinessCases}
            size={tile.previewSize}
            naturalRows={4}
            isPreview
          />
        </Box>
      </Card>
    ))
    .with('latest-interests', () => (
      <Card
        title={i18n.t('page.homepage.labels.latestInterests')}
        hasSeparator={false}
        isFullHeight
      >
        <Box minHeight={45} height="100%">
          <LatestInterestRows
            interests={DASHBOARD_TILE_PREVIEW_FIXTURES.latestInterests}
            size={tile.previewSize}
            naturalRows={4}
            isPreview
          />
        </Box>
      </Card>
    ))
    .with('opportunities-funnel', () => (
      <Card isFullHeight>
        <VStack spacing={4} height="100%">
          <Stack direction={['column', 'row']}>
            <Heading size={4}>{i18n.t('page.homepage.labels.currentOpportunities')}</Heading>
            <Show onTablet onNotebook onDesktop>
              <Space fillAvailable />
            </Show>
            <Stack direction={['column', 'row']} spacing={2}>
              <Segment
                value="total"
                options={OPPORTUNITIES_FUNNEL_SEGMENTS.owner}
                onChange={noop}
                minWidth={SEGMENT_MIN_WIDTH}
              />
              <Segment
                value="selling"
                options={OPPORTUNITIES_FUNNEL_SEGMENTS.type}
                onChange={noop}
                minWidth={SEGMENT_MIN_WIDTH}
              />
            </Stack>
          </Stack>
          <OpportunitiesFunnelMetrics
            columns={createOpportunitiesFunnelColumns(
              DASHBOARD_TILE_PREVIEW_FIXTURES.saleFunnel,
              true,
              true,
              true,
              'selling'
            )}
            size={tile.previewSize}
            isRedirecting={false}
            onSelect={noop}
          />
        </VStack>
      </Card>
    ))
    .with('my-tasks', () => (
      <Card title={i18n.t('entity.task.labels.myTasks')} hasSeparator={false} isFullHeight>
        <TaskCenterList
          data-testid="dashboardHiddenTilePreview-myTasks"
          list={DASHBOARD_TILE_PREVIEW_FIXTURES.myTasks}
          isLoading={false}
          isError={false}
          hideAssignee
          onItemClick={noop}
          isFullHeight
        />
      </Card>
    ))
    .with('notifications', () => (
      <Card
        title={i18n.t('page.homepage.labels.myNotifications')}
        hasSeparator={false}
        isFullHeight
      >
        <NotificationCenter
          notifications={DASHBOARD_TILE_PREVIEW_FIXTURES.notifications}
          onNavigate={noop}
          isHeaderHidden
        />
      </Card>
    ))
    .with('support', () => <Support />)
    .with('recommended-actions', () => <RecommendedActions size={tile.previewSize} />)
    .exhaustive();
}
